import { Navigate, Outlet, useRoutes } from 'react-router-dom';
// auth
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// layouts
import MainLayout from '../layouts/main';
import EndUserLayout from '../layouts/main/SellerLayout';
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
import DashboardRedirect from '../components/DashboardRedirect';
import {
  MainPageHoc,
  Page404,
  MembershipClubManagementPage,
  HomePage,
  LoginPage,
  ResetPasswordPage,
  NewPasswordPage,
  TwoFactorVerificationPage,
  AcceptInvitePage,
  EventPage,
  EventsPage,
  SubscriptionPage,
  NewEventPage,
  EditEventPage,
  TicketPage,
  BasketOrderPage,
  SaleStatsPage,
  CouponsPage,
  SalesTablePage,
  SeasonTicketsDiscountPage,
  // LinksPage,
  OrdersPage,
  FreeTicketsPage,
  EventSettingsPage,
  ProductionSettingsPage,
  PaymentSettingsPage,
  TimeSlotSettingsPage,
  CollaboratesPage,
  ProfileSettingsPage,
  SeatsChartPage,
  TicketsPage,
  MySellersPage,
  MyMembersPage,
  MyLeadsPage,
  VisitsTimelineReport,
  SeatsChartAdminPage,
  SubAccountsPage,
  MarketSaleStatsPage,
  PartnerOnBoardingPage,
  StartSellingPage,
  SellerPublicPage,
  SellerScannerPage,
  SellerSubscriptionsPublicPage,
  SellerSubscriptionPurchase,
  SellerPublicPageMyAccount,
  SellerPublicPageSignIn,
  SupportPage,
  EndUsersPage,
  SingleEndUserPage, SeasonTicketPage,
  GeneralAppPage,
  PublicEventsPage,
  PublicEventsTablePage,
  EventScannerPage,
  SessionScannerPage,
  LegalPage,
  TopUpPage,
  CalendarPage,
  CashierPage,
  SellerEntryPassPage,
  PromoterPage,
  PromoterLinkDetailsPage,
  IllustrationsPage,
  TimeSlotDemo,
} from './elements';
import TimeSlotsMockSimple from '../pages/demo/TimeSlotsMockSimple';
import { DashboardProvider } from "../context-state/DashboardContext";
import { EventProvider } from "../context-state/EventContext";
import { SubscriptionProvider } from '../context-state/SubscriptionContext';
import { EventGuard } from "../pages/common/guards/EventGuard";
import { AllEventsGuard } from "../pages/common/guards/AllEventsGuard";
import { TicketProvider } from "../context-state/TicketContext";
import { SubAccountGuard } from "../pages/dashboard/sub-accounts/SubAccountGuard";
import { PermissionProvider } from "../context-state/PermissionContext";
import { CollaboratesGuard } from "../pages/common/collaborates/CollaboratesGuard";
import { MarketplaceProvider } from "../context-state/MarketplaceContext";
import { MySellersGuard } from "../pages/dashboard/my-sellers/guards/MySellersGuard";
import { SeatsChartGuard } from "../pages/dashboard/seats-chart/common/guards/SeatsChartGuard";
import { MarketSaleStatsGuard } from "../pages/dashboard/admin/guards/MarketSaleStatsGuard";
import { SellerPageContextProvider } from "../context-state/SellerPageContext.jsx";
import { SellerPublicPageGuard } from "../pages/seller-public/guards/SellerPublicPageGuard.jsx";
import { RoleBasedRouteGuard, ROLES_LABELS } from "../pages/common/guards/RoleGuard.jsx";
import { EndUsersGuard } from "../pages/dashboard/end-users/guards/EndUsersGuard.jsx";
import { EndUsersProvider } from "../context-state/EndUsersContext.jsx";
import { SingleEndUserGuard } from "../pages/dashboard/end-users/guards/SingleEndUserGuard.jsx";
import { MyMembersGuard } from '../pages/dashboard/my-members/MyMembersGuard';
import LinksPage from "../pages/dashboard/links/LinksPage.jsx";
import ManageFamilyPage from '../pages/seller-public/ManageFamilyPage';
import { SeasonTicketProvider } from "../context-state/SeasonTicketContext.jsx";
import { SeasonTicketGuard } from "../pages/dashboard/season-ticket/guards/SeasonTicketGuard.jsx";
import { DashboardGuard } from "../pages/common/guards/DashboardGuard.jsx";
import { GeneralAppProvider } from "../context-state/GeneralAppContext.jsx";
import { SmsCampaignProvider } from "../context-state/SmsCampaignContext.jsx";
import { SmsCampaignGuard } from "../pages/dashboard/sms-campaig/guards/SmsCampignGuard.jsx";
import SmsCampaign from "../pages/dashboard/sms-campaig/SmsCampaign.jsx";
import { PromoterGuard } from "../pages/dashboard/promoter/guards/PromoterGuard";

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'reset-password',
          element: (
            <GuestGuard>
              <ResetPasswordPage />
            </GuestGuard>
          ),
        },
        {
          path: 'new-password',
          element: (
            <GuestGuard>
              <NewPasswordPage />
            </GuestGuard>
          ),
        },
        {
          path: '2fa-verify',
          element: <TwoFactorVerificationPage />,
        },
        {
          path: 'accept-invitation',
          element: <AcceptInvitePage />,
        },
        // partner
        {
          path: 'partner-onboarding',
          element: <PartnerOnBoardingPage />
        },
        {
          path: 'start-selling',
          element: (<GuestGuard>
            <StartSellingPage />
          </GuestGuard>)
        },
        // {
        //   path: 'register',
        //   element: (
        //     <GuestGuard>
        //       <RegisterPage />
        //     </GuestGuard>
        //   ),
        // },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <GeneralAppProvider>
            <DashboardProvider>
              <EndUsersProvider>
                <PermissionProvider>
                  <SeasonTicketProvider>
                    <SmsCampaignProvider>
                      <DashboardLayout />
                    </SmsCampaignProvider>
                  </SeasonTicketProvider>
                </PermissionProvider>
              </EndUsersProvider>
            </DashboardProvider>
          </GeneralAppProvider>
        </AuthGuard>
      ),
      children: [
        { element: <DashboardRedirect />, index: true },
        {
          path: 'main',
          element: <DashboardGuard>
            <MainPageHoc />
          </DashboardGuard>
        },
        {
          path: 'calendar',
          element: <CalendarPage />
        },
        {
          path: 'cashier',
          element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.READER}><CashierPage /></RoleBasedRouteGuard>
        },
        {
          path: 'promoter',
          element: <PromoterGuard />,
          children: [
            { element: <PromoterPage />, index: true },
            { path: ':utm', element: <PromoterLinkDetailsPage /> }
          ]
        },
        {
          path: 'profile',
          element: <ProfileSettingsPage />
        },
        {
          path: 'my-sellers',
          element: <MySellersGuard><MySellersPage /></MySellersGuard>
        },
        {
          path: 'sms-campaign',
          element: <SmsCampaignGuard><SmsCampaign /></SmsCampaignGuard>
        },
        {
          path: 'legal',
          element: <LegalPage />
        },
        {
          path: 'seats-chart-admin',
          element: <SeatsChartGuard><SeatsChartAdminPage /></SeatsChartGuard>
        },
        {
          path: 'market-sale-stats',
          element: <MarketSaleStatsGuard><MarketSaleStatsPage /></MarketSaleStatsGuard>
        },
        {
          path: 'season-ticket',
          element: <SeasonTicketGuard><SeasonTicketPage /></SeasonTicketGuard>
        },
        {
          path: 'end-users',
          element: (
            <EndUsersGuard>
              <SeasonTicketGuard>
                <Outlet />
              </SeasonTicketGuard>
            </EndUsersGuard>
          ),
          children: [
            {
              index: true,
              element: <EndUsersPage />,
            },
            {
              path: ':endUserId',
              element: <SingleEndUserGuard><SingleEndUserPage /></SingleEndUserGuard>,
            }
          ],
        },
        {
          path: 'my-members',
          element: <MyMembersGuard><MyMembersPage /></MyMembersGuard>
        },
        {
          path: 'visits-timeline',
          element: <MyMembersGuard><VisitsTimelineReport /></MyMembersGuard>
        },
        {
          path: 'my-leads',
          element: <MyLeadsPage />
        },
        {
          path: 'payment-settings',
          element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.ADMIN}><PaymentSettingsPage /></RoleBasedRouteGuard>
        },
        {
          path: 'sub',
          children: [
            { element: <SubAccountsPage />, index: true },
            {
              path: ':subId',
              element: <SubAccountGuard />,
              children: [
                {
                  path: 'collaborate',
                  element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.ADMIN}><CollaboratesGuard><CollaboratesPage /></CollaboratesGuard></RoleBasedRouteGuard>
                },
                {
                  path: 'coupons',
                  element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.READER}><CouponsPage isProduction={true}/></RoleBasedRouteGuard>
                },
                {
                  path: 'sales-table',
                  element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.READER}><SalesTablePage /></RoleBasedRouteGuard>
                },
                {
                  path: 'production-settings',
                  element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.READER}><ProductionSettingsPage /></RoleBasedRouteGuard>
                },
                {
                  path: 'payment-settings',
                  element: <RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.EDITOR}><PaymentSettingsPage /></RoleBasedRouteGuard>
                },
                {
                  path: 'event',
                  element: <AllEventsGuard refetch={['/tickets']} />,
                  children: [
                    { element: <EventsPage />, index: true },
                    { path: 'new', element: <NewEventPage /> },
                    {
                      path: ':eventId',
                      // We wrap here with the EventProvider cause we need the checkout functionality of the event page inside the orders module (is it good practice? im not sure, but it works for now)
                      element: <EventProvider><RoleBasedRouteGuard minimumRoleAllowedAccess={ROLES_LABELS.READER}><EventGuard /></RoleBasedRouteGuard></EventProvider>,
                      children: [
                        // { element: <SingleEventDashboard />, index: true },
                        { path: 'edit', element: <EditEventPage /> },
                        { path: 'sell-stats', element: <SaleStatsPage /> },
                        { path: 'coupons', element: <CouponsPage  /> },
                        { path: 'free-tickets', element: <FreeTicketsPage /> },
                        { path: 'season-tickets-discount', element: <SeasonTicketGuard><SeasonTicketsDiscountPage /></SeasonTicketGuard> },
                        { path: 'links', element: <LinksPage /> },
                        { path: 'orders', element: <OrdersPage /> },
                        { path: 'settings', element: <EventSettingsPage /> },
                        { path: 'time-slots', element: <TimeSlotSettingsPage /> },
                        { path: 'seats-chart', element: <SeatsChartGuard><SeatsChartPage /></SeatsChartGuard> },
                        { path: 'tickets', element: <TicketsPage /> },
                      ]
                    },
                  ]
                },
              ]
            }
          ]
        }
      ],
    },

    // Main Routes
    {
      element: <MainLayout />,
      context: 'eventPage',
      children: [
        { element: <HomePage />, index: true },
        { path: 'clubs', element: <MembershipClubManagementPage /> },
        // { path: 'illustrations', element: <IllustrationsPage /> },
      ],
    },
    {
      element: (<SellerPageContextProvider>
        <SellerPublicPageGuard>
          <EndUserLayout />
        </SellerPublicPageGuard>
      </SellerPageContextProvider>
      ),
      context: 'sellerPage',
      children: [
        { path: 'seller/:handler', element: <SellerPublicPage /> },
        { path: 'seller/:handler/events', element: <SellerPublicPage /> },
        { path: 'seller/:handler/signin', element: <SellerPublicPageSignIn /> },
        { path: 'seller/:handler/signup', element: <SellerPublicPageSignIn /> },
        { path: 'seller/:handler/subscriptions', element: <SellerSubscriptionsPublicPage /> },
        { path: 'seller/:handler/entry-pass', element: <SellerEntryPassPage /> },
        { path: 'seller/:handler/subscription/:subscriptionId', element: <SellerSubscriptionPurchase /> },
        { path: 'seller/:handler/my-account', element: <SellerPublicPageMyAccount /> },
        { path: 'seller/:handler/manage-family/:subscriptionId', element: <ManageFamilyPage /> },
      ]
    },
    // events page
    {
      children: [
        { path: 'event/*', element: <EventProvider><EventPage /></EventProvider> },
      ]
    },
    {
      children: [
        { path: 'events/:type/:id', element: <PublicEventsPage /> },
      ]
    },
    {
      children: [
        { path: 'events-table/:type/:id', element: <PublicEventsTablePage /> },
      ]
    },
    {
      children: [
        { path: 'scanner/:eventId', context: 'scanner', element: <EventScannerPage /> },
        { path: 'scanner/session/:sessionId', element: <SessionScannerPage /> },
      ]
    },
    // seller scanner page
    {
      children: [
        { path: 'seller-scanner/:sellerId/:subscriptionId', element: <SellerScannerPage /> },
        { path: 'seller-scanner/:sellerId', element: <SellerScannerPage /> },
      ]
    },
    // Subscription Page
    {
      children: [
        { path: 'subscription/*', element: <SubscriptionProvider><SubscriptionPage /></SubscriptionProvider> },
      ]
    },
    // Ticket Page
    {
      children: [
        { path: 'ticket/*', context: 'ticketPage', element: <TicketProvider><TicketPage /></TicketProvider> },
      ]
    },
    // Basket Order Page
    {
      children: [
        { path: 'basket-order/*', element: <BasketOrderPage /> },
      ]
    },
    // Demo Pages
    {
      children: [
        { path: 'demo/timeslots', element: <EventProvider><TimeSlotDemo /></EventProvider> },
        { path: 'demo/timeslots-mock', element: <TimeSlotsMockSimple /> },
      ]
    },
    {
      children: [
        { path: 'top-up/:accountHandler', element: <TopUpPage /> },
      ]
    },
    {
      element: <CompactLayout />,
      children: [
        { path: '404', element: <Page404 /> },
      ],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'support', element: <SupportPage /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },

  ]);
}
